import { useCallback, type RefObject } from "react";

import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import { captureDesktopChatAuthScopeGuard } from "./desktop-chat-auth-scope";
import { prepareScreenRecordings } from "./screen-recording-attachments";
import { activeAgentSupportsScreenshotAttachment } from "./use-screenshot-attach";
import {
  screenRecordingTextSessionIds,
  useScreenRecordingStore,
} from "./screen-recording-store";

export function useScreenRecordingDraft(
  ctxRef: RefObject<CursorInputContextValue>,
) {
  const hasRecordings = useScreenRecordingStore((state) =>
    Object.values(state.clips).some(
      (clip) => state.sessions[clip.attentionSessionId] === "text",
    ),
  );
  const prepare = useCallback(async () => {
    const scope = useScreenRecordingStore.getState().textScope;
    const projectId = ctxRef.current.projectId;
    const currentAuth = captureDesktopChatAuthScopeGuard();
    const cancelled = () =>
      !currentAuth() ||
      useScreenRecordingStore.getState().textScope !== scope ||
      ctxRef.current.projectId !== projectId;
    const prepared = await prepareScreenRecordings(
      screenRecordingTextSessionIds(),
      ctxRef.current.attachedImages.length,
      cancelled,
      activeAgentSupportsScreenshotAttachment(),
    );
    if (cancelled())
      throw new Error("The recording draft changed before it could be sent.");
    return prepared;
  }, [ctxRef]);
  return { hasRecordings, prepare };
}
